<template>
  <div
    class="fixed flex flex-col rounded-lg border border-white/10 bg-[var(--surface-color,#0d0e12)] shadow-2xl overflow-hidden"
    :style="frameStyle"
    @pointerdown="onFramePointerDown"
  >
    <!-- Header / drag handle -->
    <div
      ref="headerEl"
      class="flex items-center gap-2 px-2 h-9 border-b border-white/5 select-none cursor-grab active:cursor-grabbing bg-black/30"
    >
      <el-icon v-if="isGroup" size="12" class="text-primary"><UserFilled /></el-icon>
      <span class="text-xs font-semibold text-white truncate flex-1">{{ headerLabel }}</span>
      <button
        type="button"
        class="p-1 rounded hover:bg-white/10 text-text-muted hover:text-white"
        :title="$t('chat.detach.reattach')"
        :aria-label="$t('chat.detach.reattach')"
        data-no-drag
        @click="reattach(threadId)"
      >
        <el-icon size="12"><CopyDocument /></el-icon>
      </button>
    </div>

    <!-- Thread view (reuses the normal ChatThreadView — no special detached mode) -->
    <div class="flex-1 min-h-0 flex flex-col">
      <ChatThreadView
        :thread-id="threadId"
        @back="reattach(threadId)"
      />
    </div>

    <!-- Resize grip (bottom-right corner) -->
    <div
      ref="gripEl"
      class="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize"
      data-no-drag
      :aria-label="$t('chat.detach.resize')"
    >
      <svg viewBox="0 0 10 10" class="w-full h-full text-white/30">
        <path d="M 0 10 L 10 0 M 4 10 L 10 4 M 8 10 L 10 8" stroke="currentColor" stroke-width="1" fill="none" />
      </svg>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { UserFilled, CopyDocument } from '@element-plus/icons-vue'
import { useDragResize } from '~/composables/useDragResize'
import { useDetachedWindows } from '~/composables/chat/useDetachedWindows'
import { useChatThreads } from '~/composables/chat/useChatThreads'

/**
 * Floating, draggable + resizable wrapper around `ChatThreadView`.
 *
 * The drag+resize frame state (x/y/width/height) lives in `useDragResize`
 * locally; committed rects persist to `useDetachedWindows` (localStorage)
 * so reloading restores the layout.
 *
 * Z-index reflects the singleton's per-thread `z` value — clicking anywhere
 * in the window bumps it above the other detached windows.
 */

const props = defineProps({
  state: { type: Object, required: true }
  //                       { threadId, x, y, width, height, z }
})

const threadId = computed(() => props.state.threadId)

const { reattach, focus, updateRect } = useDetachedWindows()
const { threads } = useChatThreads()

const headerEl = ref(null)
const gripEl = ref(null)

const { x, y, width, height } = useDragResize({
  headerEl,
  gripEl,
  initial: {
    x: props.state.x,
    y: props.state.y,
    width: props.state.width,
    height: props.state.height
  },
  min: { width: 300, height: 360 },
  onCommit: (rect) => updateRect(threadId.value, rect)
})

const frameStyle = computed(() => ({
  left: `${x.value}px`,
  top: `${y.value}px`,
  width: `${width.value}px`,
  height: `${height.value}px`,
  zIndex: props.state.z
}))

// Thread display metadata — matches the in-panel ThreadView fallbacks
const thread = computed(() =>
  threads.value.find(t => t.id === threadId.value) || null
)
const isGroup = computed(() => thread.value?.kind === 'group')
const headerLabel = computed(() => {
  if (!thread.value) return 'Chat'
  return thread.value.kind === 'group'
    ? (thread.value.title || 'Group')
    : (thread.value.otherUser?.name || 'User')
})

// Raise-to-front on any interaction inside the frame
function onFramePointerDown() {
  focus(threadId.value)
}
</script>
